import { SettlementStage } from "../types/card-movements.types";

/**
 * Drops the settlement echo of a purchase the issuer reports in two phases: it arrives as a second movement
 * with its own id (authorization first, then settlement at the final FX rate), so the same purchase would
 * render twice. Only the authorization is shown.
 *
 * Filtering here is safe on two counts: the card balance is read from the provider, not derived from these
 * rows, and the backend only stamps `SETTLEMENT` once it has found the authorization the row settles — an
 * orphan settlement arrives unstaged and therefore stays visible.
 *
 * Pure and order-preserving so callers can derive it at render time and keep paginating over the unfiltered
 * list.
 */
export function visibleCardMovements<T extends { settlement_stage: SettlementStage | null }>(
  movements: T[],
): T[] {
  return movements.filter((movement) => movement.settlement_stage !== SettlementStage.SETTLEMENT);
}

/**
 * Formats movement date and time in a friendly Spanish format. Example: "19 de enero de 2025, 05:11"
 *
 * @param dateInput - ISO string or Date (e.g. tx.created_at)
 * @returns Complete date: day, month name, year and time (HH:mm)
 */
export function formatMovementDateTime(dateInput: string | Date): string {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const datePart = date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
}

/**
 * Folds a fresh read of the first page into the movements on screen, for a live update: the new page goes on
 * top (a movement already shown is replaced by its newer version, so a pending purchase that settled shows
 * its new status) and every other loaded movement keeps its place below it.
 *
 * Merging instead of replacing keeps the pages loaded with "load more", and the pagination cursor
 * (`movements.length`) stays right: the movements that arrived also shifted the backend's list by as many
 * rows.
 */
export function mergeLatestMovements<T extends { id: string }>(current: T[], latest: T[]): T[] {
  if (latest.length === 0) return current;
  const latestIds = new Set(latest.map((movement) => movement.id));
  return [...latest, ...current.filter((movement) => !latestIds.has(movement.id))];
}

/**
 * Whether a fresh first page cannot be folded into the movements on screen: it is a full page and shares no
 * movement with them, so more than a page arrived since they were read (a busy card, a laptop that slept) and
 * the movements in between are on neither. The list then restarts from that page, and its pagination with
 * it.
 */
export function leavesMovementGap<T extends { id: string }>(
  current: T[],
  latest: T[],
  pageSize: number,
): boolean {
  if (current.length === 0 || latest.length < pageSize) return false;
  const currentIds = new Set(current.map((movement) => movement.id));
  return !latest.some((movement) => currentIds.has(movement.id));
}

/**
 * Appends a page read with "load more", dropping the movements already shown. A movement that arrived after
 * the list was read shifts the backend's pages by one, so the next page repeats the last row; without this it
 * would show twice, under the same React key.
 */
export function appendMovementPage<T extends { id: string }>(current: T[], page: T[]): T[] {
  const currentIds = new Set(current.map((movement) => movement.id));
  return [...current, ...page.filter((movement) => !currentIds.has(movement.id))];
}
